import { useQuery, useApolloClient } from '@apollo/client';
import { USER_QUERY } from '@app/apollo/queries';
import { getToken } from '@app/config/apollo/helpers';
import { useCallback, useMemo } from 'react';

export const useUser = () => {
  const client = useApolloClient();

  // More robust token validation
  const getValidToken = useCallback(() => {
    try {
      const token = getToken()();
      // Check if token exists and is not empty/null/undefined
      if (!token || token.trim() === '' || token === 'null' || token === 'undefined') {
        return null;
      }
      return token;
    } catch (error) {
      console.warn('Error getting token:', error);
      return null;
    }
  }, []);

  const token = getValidToken();
  const shouldFetch = useMemo(() => {
    // Only fetch if we have a valid token
    if (!token) return false;

    // Additional check: ensure we're not on a completely public page where user data isn't needed
    // You can add more conditions here based on your app's routing logic
    return true;
  }, [token]);

  const { data: fetchedData, loading, error: userError, refetch } = useQuery(USER_QUERY, {
    skip: !shouldFetch,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: false,
    returnPartialData: true,
    // Add onCompleted and onError for debugging
    onCompleted: (data) => {
      if (data?.me) {
        console.log('✅ User data fetched successfully');
      }
    },
    onError: (error) => {
      // If token is invalid, don't spam console
      if (error.message.includes('Unauthenticated') || error.message.includes('invalid')) {
        console.warn('⚠️ Authentication token may be invalid');
      } else {
        console.error('❌ Error fetching user:', error);
      }
    }
  });

  const user = useMemo(() => fetchedData?.me || null, [fetchedData?.me]);
  const isAuthenticated = useMemo(() => !!user && !!token, [user, token]);

  // Enhanced cache methods with better error handling
  const updateUserInCache = useCallback((updatedUser) => {
    if (!updatedUser) {
      console.warn('Cannot update user cache with null/undefined data');
      return;
    }

    try {
      client.writeQuery({
        query: USER_QUERY,
        data: { me: updatedUser }
      });
    } catch (error) {
      console.warn('Failed to update user in cache:', error);
    }
  }, [client]);

  const setUserInCache = useCallback((userData) => {
    if (!userData) {
      console.warn('Cannot set user cache with null/undefined data');
      return;
    }

    try {
      client.writeQuery({
        query: USER_QUERY,
        data: { me: userData }
      });
    } catch (error) {
      console.warn('Failed to set user in cache:', error);
    }
  }, [client]);

  const clearUserFromCache = useCallback(() => {
    try {
      client.writeQuery({
        query: USER_QUERY,
        data: { me: null }
      });
      client.cache.evict({ fieldName: 'me' });
      client.cache.gc();
      console.log('✅ User cache cleared');
    } catch (error) {
      console.warn('Failed to clear user from cache:', error);
    }
  }, [client]);

  const getCachedUser = useCallback(() => {
    try {
      const cachedData = client.readQuery({
        query: USER_QUERY,
        errorPolicy: 'ignore'
      });
      return cachedData?.me || null;
    } catch (error) {
      return null;
    }
  }, [client]);

  // Check if we have cached user data without making network call
  const hasCachedUserData = useMemo(() => {
    try {
      const cachedData = client.readQuery({
        query: USER_QUERY,
        errorPolicy: 'ignore'
      });
      return !!cachedData?.me;
    } catch (error) {
      return false;
    }
  }, [client]);

  const isUserManagerOrAdmin = useCallback((members) => {
    if (!user || !members) return false;
    return members.some(member =>
      (member?.role === 'Manager' || member?.role === 'Admin') &&
      member?.user?.id === user.id
    );
  }, [user]);

  const isOrganiserOrInvitedAdmin = useCallback((opportunity, teamMembers = null) => {
    if (!user || !opportunity) return false;

    // check if user is main organiser
    if(opportunity.user?.id === user.id) return true;

    const members = [
      teamMembers,
      opportunity.teamMembers,
      opportunity.organisation?.teamMembers,
    ].find(Array.isArray);

    if (!members) return false;

    return members.some(member =>
      (member?.role === 'Manager' || member?.role === 'Admin') &&
      member?.user?.id === user.id
    );
  }, [user]);

  const getUserRoleForEvent = useCallback((event) => {
    if (!event || !user) return null;

    if (event.user?.id === user.id) {
      return 'organizer';
    }

    const member = event.members?.find(m => m.user?.id === user.id);
    return member?.role?.toLowerCase() || null;
  }, [user]);

  const hasEventAdminAccess = useCallback((event, requiredRoles = ['organizer', 'admin', 'manager']) => {
    const userRole = getUserRoleForEvent(event);
    return requiredRoles.includes(userRole);
  }, [getUserRoleForEvent]);

  const isOrganizer = useCallback((event) => {
    if (!user || !event?.user?.id) return false;
    return event.user?.id === user.id;
  }, [user]);

  const refreshUser = useCallback(() => {
    if (!token) {
      console.warn('Cannot refresh user: no valid token');
      return Promise.resolve();
    }
    return refetch();
  }, [refetch, token]);

  // Early return for unauthenticated state (no API call made)
  if (!shouldFetch) {
    return {
      loading: false,
      isAuthenticated: false,
      user: null,
      loggedInUser: null,
      hasCachedUserData,
      isUserManagerOrAdmin,
      isOrganiserOrInvitedAdmin,
      getUserRoleForEvent,
      hasEventAdminAccess,
      isOrganizer,
      refreshUser,
      updateUserInCache,
      setUserInCache,
      clearUserFromCache,
      getCachedUser,
      error: null
    };
  }

  // Handle error state
  if (userError) {
    console.error('Error fetching user:', userError);
    return {
      loading: false,
      isAuthenticated: false,
      user: null,
      loggedInUser: null,
      hasCachedUserData,
      isUserManagerOrAdmin,
      isOrganiserOrInvitedAdmin,
      getUserRoleForEvent,
      hasEventAdminAccess,
      isOrganizer,
      refreshUser,
      updateUserInCache,
      setUserInCache,
      clearUserFromCache,
      getCachedUser,
      error: userError
    };
  }

  // Normal authenticated state
  return {
    loading,
    isAuthenticated,
    user,
    loggedInUser: user,
    hasCachedUserData,
    isUserManagerOrAdmin,
    isOrganiserOrInvitedAdmin,
    getUserRoleForEvent,
    hasEventAdminAccess,
    isOrganizer,
    refreshUser,
    updateUserInCache,
    setUserInCache,
    clearUserFromCache,
    getCachedUser,
    error: null
  };
};
